/** The tour's phone preview where it is drawn — the dock's Preview segment, or the narrow
 *  layout's drawer (plan §4). The render state is the page's (`TourPreviewProvider` over
 *  the records' `RecordPreviewProvider`); this is the view of it:
 *
 *  - nothing renders until Render (plan §4: on demand only); the draft's version makes a
 *    render "Out of date" — Render compiles the draft then;
 *  - it follows the selection: on the Itinerary tab the phone shows the selected stop's
 *    day; the Tour tab shows the trip screen as the phone left it;
 *  - what the render cannot show is said in words: staged files, changes the app never
 *    draws, and — one click away — everything else it leaves out. */
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Box, Typography } from '@mui/material';
import { useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { MobilePreviewView } from '@/components/MobilePreview';
import { PreviewLimitsDisclosure } from '@/components/PreviewLimits/PreviewLimits';
import { draftCount } from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { useRecordPreview } from '@/features/records/preview/RecordPreviewProvider';
import { usePreviewDayIndex } from '@/features/records/preview/target';

import { PREVIEW_LIMITS, previewDraftNotes } from './notes';

export interface TourPreviewPaneProps {
  /** The page tab: the Itinerary tab's selection steers the phone's day. */
  tab: 'tour' | 'itinerary';
}

export function TourPreviewPane({ tab }: TourPreviewPaneProps) {
  const { controller, canDraft, target, screen, setScreen, updatedTs } = useRecordPreview();
  const { store } = useDraftStable();
  const version = useSyncExternalStore(store.subscribe, () => store.getState().version);
  const dayIndex = usePreviewDayIndex(target);

  // Re-read on every draft change and on a reloaded tour (its `updated_ts`).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- version / updatedTs are the inputs of the getter
  const fingerprint = useMemo(() => controller.currentFingerprint(), [controller, version, updatedTs]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed per draft change, read from the store
  const draftNotes = useMemo(() => previewDraftNotes(store.getState()), [store, version]);
  // A draft Render would apply — not merely the permission to draft.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed per draft change, read from the store
  const hasDraft = useMemo(() => canDraft && draftCount(store.getState()) > 0, [canDraft, store, version]);

  return (
    <MobilePreviewView
      controller={controller}
      fingerprint={fingerprint}
      hasDraft={hasDraft}
      {...(tab === 'itinerary' ? { dayIndex } : {})}
      {...(screen !== undefined ? { screen } : {})}
      onScreen={setScreen}
      // The Itinerary tab's phone follows the selected day: the trip screen, always.
      {...(tab === 'itinerary' ? { screenLocked: 'The Itinerary tab shows the selected day' } : {})}
      notes={<PreviewNotes staged={draftNotes.staged} cleared={draftNotes.cleared} undrawn={canDraft ? draftNotes.undrawn : []} />}
    />
  );
}

function Note({ icon, children, kind }: { icon: ReactNode; children: ReactNode; kind: string }) {
  return (
    <Typography
      variant="caption"
      component="p"
      data-preview-note={kind}
      sx={{ m: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start', '& > svg': { fontSize: 16, mt: '1px', flex: 'none', color: 'text.secondary' } }}
    >
      {icon}
      <span>{children}</span>
    </Typography>
  );
}

function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function PreviewNotes({ staged, cleared, undrawn }: { staged: string[]; cleared: number; undrawn: string[] }) {
  return (
    <Box data-preview-notes sx={{ display: 'grid', gap: 0.5 }}>
      {(staged.length > 0 || cleared > 0) && (
        <Note icon={<AttachFileIcon aria-hidden />} kind="files">
          Files aren’t previewable
          {staged.length > 0 ? `: ${listOf(staged)} ${staged.length === 1 ? 'is' : 'are'} staged` : ''}
          {cleared > 0 ? `${staged.length > 0 ? ',' : ':'} ${cleared === 1 ? 'a file is' : `${cleared} files are`} cleared` : ''}. The phone shows
          the stored ones.
        </Note>
      )}
      {undrawn.length > 0 && (
        <Note icon={<VisibilityOffOutlinedIcon aria-hidden />} kind="undrawn">
          Not drawn by the app: your changes to {listOf(undrawn)}.
        </Note>
      )}
      <PreviewLimitsDisclosure limits={PREVIEW_LIMITS} />
    </Box>
  );
}
